import { createContext, useContext, useMemo, useState } from "react"
import type { Dispatch, SetStateAction } from "react"

type ResourceBookingConfirmDialogDataValue = Readonly<{
    confirmBookingDialog: boolean
}>

type ResourceBookingConfirmDialogActionsValue = Readonly<{
    setConfirmBookingDialog: Dispatch<SetStateAction<boolean>>
}>

export type ResourceBookingConfirmDialogContextValue = ResourceBookingConfirmDialogDataValue &
    ResourceBookingConfirmDialogActionsValue

const ResourceBookingConfirmDialogDataContext = createContext<ResourceBookingConfirmDialogDataValue | null>(null)
const ResourceBookingConfirmDialogActionsContext = createContext<ResourceBookingConfirmDialogActionsValue | null>(null)

type ResourceBookingConfirmDialogProviderProps = Readonly<{
    children: React.ReactNode
}>

export function ResourceBookingConfirmDialogProvider({ children }: ResourceBookingConfirmDialogProviderProps) {
    const [confirmBookingDialog, setConfirmBookingDialog] = useState<boolean>(false)

    const dataValue = useMemo<ResourceBookingConfirmDialogDataValue>(
        () => ({ confirmBookingDialog }),
        [confirmBookingDialog],
    )

    const actionsValue = useMemo<ResourceBookingConfirmDialogActionsValue>(
        () => ({ setConfirmBookingDialog }),
        [setConfirmBookingDialog],
    )

    return (
        <ResourceBookingConfirmDialogDataContext.Provider value={dataValue}>
            <ResourceBookingConfirmDialogActionsContext.Provider value={actionsValue}>
                {children}
            </ResourceBookingConfirmDialogActionsContext.Provider>
        </ResourceBookingConfirmDialogDataContext.Provider>
    )
}

export function useConfirmBookingDialogData(): ResourceBookingConfirmDialogDataValue {
    const context = useContext(ResourceBookingConfirmDialogDataContext)
    if (!context) {
        throw new Error("useConfirmBookingDialogData must be used within ResourceBookingConfirmDialogProvider")
    }
    return context
}

export function useConfirmBookingDialogActions(): ResourceBookingConfirmDialogActionsValue {
    const context = useContext(ResourceBookingConfirmDialogActionsContext)
    if (!context) {
        throw new Error("useConfirmBookingDialogActions must be used within ResourceBookingConfirmDialogProvider")
    }
    return context
}

export function useConfirmBookingDialog(): ResourceBookingConfirmDialogContextValue {
    const data = useContext(ResourceBookingConfirmDialogDataContext)
    const actions = useContext(ResourceBookingConfirmDialogActionsContext)
    if (!data || !actions) {
        throw new Error("useConfirmBookingDialog must be used within ResourceBookingConfirmDialogProvider")
    }
    return { ...data, ...actions }
}
